<script setup lang="ts">
import { computed } from 'vue';
import ProviderBadge from './ProviderBadge.vue';
import HitTypeTag from './HitTypeTag.vue';
import {
  AGE_TONE_CLASSES,
  ageInQueueLabel,
  ageInQueueTone,
  ageInQueueTooltip,
} from '@/util/ageInQueue';
import { initials, avatarHue } from '@/util/avatar';
import type { QueueRowData } from './queueRow';

const props = defineProps<{ row: QueueRowData }>();

const age = computed(() => ageInQueueLabel(props.row.createdAt));
const ageTone = computed(() => ageInQueueTone(props.row.createdAt));
const ageTooltip = computed(() => ageInQueueTooltip(props.row.createdAt));
const hue = computed(() => avatarHue(props.row.name));
const hitsSummary = computed(() => {
  const { hitsTotal, hitsOpen } = props.row;
  return `${hitsTotal} ${hitsTotal === 1 ? 'hit' : 'hits'} · ${hitsOpen} open`;
});
</script>

<template>
  <RouterLink
    :to="`/aml-review/${row.id}`"
    data-test="queue-card"
    :aria-label="`Review ${row.name}`"
    class="block rounded-lg border border-gray-200 bg-white p-3.5 active:bg-gray-50"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex h-9 w-9 flex-none items-center justify-center rounded-full text-xs font-bold"
        :style="{ backgroundColor: `hsl(${hue} 90% 94%)`, color: `hsl(${hue} 60% 32%)` }"
      >
        {{ initials(row.name) }}
      </div>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-gray-900">{{ row.name }}</div>
        <div class="truncate text-xs text-gray-500">{{ row.partner }}</div>
      </div>
      <ProviderBadge :provider="row.provider" />
    </div>
    <div class="mt-3 flex flex-wrap items-center gap-1">
      <HitTypeTag v-for="hitType in row.hitTypes" :key="hitType" :hit-type="hitType" />
      <span
        class="inline-flex w-fit items-center rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-gray-700 uppercase"
      >
        {{ row.region }}
      </span>
    </div>
    <div class="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5">
      <span class="text-xs text-gray-500">{{ hitsSummary }}</span>
      <span class="group/age relative">
        <span
          class="inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
          :class="AGE_TONE_CLASSES[ageTone]"
        >
          {{ age }}
        </span>
        <span
          role="tooltip"
          class="pointer-events-none absolute right-0 bottom-full z-30 mb-1 rounded-md bg-gray-900 px-2 py-1 text-[11px] whitespace-nowrap text-white opacity-0 transition-opacity duration-100 group-hover/age:opacity-100"
        >
          {{ ageTooltip }}
          <span
            class="absolute top-full right-2 border-4 border-transparent border-t-gray-900"
          ></span>
        </span>
      </span>
    </div>
  </RouterLink>
</template>
